'use client';

import classNames from 'classnames';
import Link from 'next/link';
import { useState } from 'react';

import { EVENT_NAMES, trackEvent } from '@/analytics';
import { assignTopicMenuGroupColor } from '@/components/SiteHeader/sharedConfig';
import { Typography } from '@/components/Typography';
import { topicPath } from '@/helpers/linkHelpers';
import ArrowRight from '@/icons/react/ArrowRight';
import Minus from '@/icons/react/Minus';
import Plus from '@/icons/react/Plus';
import type { SiteNavMenuItemType } from '@/lib/firestore/dataFetchers/getSiteNavMenu';
import { useSiteHeader } from '@/providers';

interface MobileMenuToggleGroupProps {
  category?: SiteNavMenuItemType;
  childrenTopics?: SiteNavMenuItemType[];
  comingSoonTopics?: SiteNavMenuItemType[];
  menuIndex?: number;
  showNewTag?: boolean;
}

export function MobileMenuToggleGroup({
  category,
  childrenTopics,
  comingSoonTopics,
  menuIndex,
  showNewTag = false,
}: MobileMenuToggleGroupProps) {
  const [open, setOpen] = useState(false);
  const { setMobileMenuOpen } = useSiteHeader();

  function handleCategoryClick() {
    setOpen(!open);

    trackEvent(EVENT_NAMES.site_nav_dropdown_opened, {
      dropdown_label: category?.title,
      topic_slug: category?.slug,
      menu_position_index: menuIndex?.toString(), // TODO adjust if needed when Daily News is present
      window_size: 'mobile',
    });
  }

  function handleTopicLinkClick(
    topicSlug: string | undefined,
    categorySlug: string | undefined,
    label: string,
    index?: number,
  ) {
    setMobileMenuOpen(false);

    const level = categorySlug ? 'parent' : 'child';

    trackEvent(EVENT_NAMES.site_nav_topic_click, {
      link_label: label,
      topic_slug: topicSlug,
      topic_level: level,
      parent_topic_slug: categorySlug, // legacy name to keep reports consistent over time
      path: categorySlug ? topicPath(categorySlug) : topicPath(topicSlug),
      menu_position_index: index?.toString(),
      window_size: 'mobile',
    });
  }

  const accentColors = assignTopicMenuGroupColor(menuIndex);

  return (
    <div
      className={classNames('flex flex-col', {
        [accentColors.bg]: open,
      })}
    >
      <button
        type="button"
        className="flex items-center gap-3 px-4 py-3"
        onClick={handleCategoryClick}
      >
        <Typography name="body-550" className="font-bold">
          {category?.title}
        </Typography>
        {showNewTag && (
          <Typography
            name="tag-300"
            className="bg-purple-500 text-paper rounded-full px-2 py-0"
          >
            New
          </Typography>
        )}
        {open ? (
          <Minus className="text-gray-500" />
        ) : (
          <Plus className="text-gray-500" />
        )}
      </button>
      {open && (
        <div className="border border-dashed border-primary mx-4 mb-4 rounded-2 flex flex-col gap-2">
          <Link
            href={topicPath(category?.slug)}
            className={classNames(
              'flex items-center justify-between px-2 py-1 mx-2 mt-2 rounded-1',
              accentColors.btn,
            )}
            onClick={() =>
              handleTopicLinkClick(
                undefined,
                category?.slug || '',
                category?.title || '',
                0,
              )
            }
          >
            View all {category?.title}
            <ArrowRight />
          </Link>
          <ul className="columns-2 px-2 pb-2">
            {childrenTopics?.map((topic, index) => (
              <li key={topic.id} className="block">
                <Link
                  href={topicPath(topic.slug)}
                  className="block p-2 text-link"
                  onClick={() =>
                    handleTopicLinkClick(
                      topic?.slug,
                      undefined,
                      topic?.title,
                      index + 1,
                    )
                  }
                >
                  {topic.title}
                </Link>
              </li>
            ))}
          </ul>
          {!!comingSoonTopics?.length && (
            <div className="flex flex-col gap-2 pb-4 px-4">
              <Typography name="tag-350" className="text-secondary">
                Coming Soon
              </Typography>
              <Typography className="text-secondary italic" name="body-400">
                {comingSoonTopics.map((topic) => topic.title).join(', ')} and
                much more
              </Typography>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
